import {
  Box,
  Flex,
  Icon,
  InlineNotification,
  TableIconButton,
  Td,
  Text,
  Tr,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import React, { useCallback, useState } from "react";
import { DeleteButtonNew } from "../../../modules/DeleteButtonNew";
import { useSessionStore } from "../../../storez/SessionStore";
import { GroupUMS } from "../../../types/Groups";
import { deleteGroup, searchGroups } from "../../../utils/api/Groups";
import Link from "../../Link";
import TableBasedPage from "../../templates/TableBasedPage";
import ArchivedStatusPill from "../ArchivedStatusPill";
import { IdentitySearch, IdentitySearchParams } from "../IdentitySearch/IdentitySearch";
import GroupsAddModal from "./GroupsAddModal";
import GroupsPurgeModal from "./GroupsPurgeModal";

const GroupsTable: React.FC = () => {
  const PAGE_SIZE = 50;
  const { t } = useTranslation();
  const [tableResults, setTableResults] = useState<GroupUMS[]>([]);
  const [loading, setLoading] = useState(true);
  const { successToast, errorToast } = useToast();
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();
  const [refresh, setRefresh] = useState(false);

  const getInitialSearch = (): string => {
    const queryString = window.location.search || window.location.hash.split("?")[1] || "";
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get("groupname") || "";
  };

  const initialSearch = getInitialSearch();

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Users and Groups"), url: "/config/device/userdb", isActive: false },
    { title: t("Groups"), isActive: true },
  ];

  const columns = [
    { headerText: t("Provider Name"), columnName: "name", sortable: true, searchable: true },
    { headerText: t("Provider Description"), columnName: "description", sortable: true, searchable: true },
    { headerText: t("Provider DN"), columnName: "distinguishedName", sortable: true, searchable: true },
    { headerText: t("Provider"), columnName: "sourceType", sortable: true, searchable: true },
    { headerText: t("Status"), columnName: "archived", sortable: true, searchable: false },
    { headerText: t("Operations"), columnName: "operations", sortable: false, searchable: false },
  ];

  const search = useCallback((params: IdentitySearchParams): Promise<GroupUMS[]> => {
    return searchGroups(params.query, true, params.pageSize);
  }, []);

  const handleDeleteGroup = async (id: string) => {
    try {
      await deleteGroup(id);
      setRefresh(true);
      successToast({ title: "Success!", description: "We've received the request and it has been sent for processing." });
    } catch (error) {
      errorToast({ title: "Error", description: "We're not able to process the request at this time. Please try again later." });
    }
  };

  const operations = (id: string, isLocal: boolean): JSX.Element => {
    // Local groups can be edited and deleted. Synced groups can only be viewed.
    if (isLocal) {
      return (
        <Flex>
          <Box mr="sp8">
            <Link to={`/config/device/userdb/groups/${id}`}>
              <TableIconButton aria-label="Edit" icon={<Icon icon="fa-pencil" variant="solid" color="text.paragraph.light" />} />
            </Link>
          </Box>
          <Box mr="sp8">
            <DeleteButtonNew
              data-testid="group-delete-button"
              onClick={() =>
                void (async () => {
                  await handleDeleteGroup(id);
                })()
              }
              titleToDelete="Group"
              bodyToDelete="group"
              actionVerb="Archive"
            />
          </Box>
        </Flex>
      );
    } else {
      return (
        <Link to={`/config/device/userdb/groups/${id}`}>
          <TableIconButton aria-label="View" icon={<Icon icon="fa-eye" variant="solid" color="text.paragraph.light" />} />
        </Link>
      );
    }
  };

  const tableDataMap = (group: GroupUMS, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="group-groupname">
          <Link to={`/config/device/userdb/groups/${group.id}`}>
            <Text fontSize={"sm"}>{group.name}</Text>
          </Link>
        </Td>
        <Td data-testid="group-description">
          <Text>{group.description}</Text>
        </Td>
        <Td data-testid="group-distinguishedName">
          <Text>{group.distinguishedName}</Text>
        </Td>
        <Td data-testid="group-sourceType">
          <Text>{group.sourceType}</Text>
        </Td>
        <Td data-testid="group-status">
          <ArchivedStatusPill archived={group.archived} />
        </Td>
        <Td>{operations(group.id, group.sourceType === "LOCAL")}</Td>
      </Tr>
    );
  };

  const aboveTableChildren = (): JSX.Element => {
    return (
      <Flex mt="sp24" mr="sp24" justifyContent="end">
        <GroupsAddModal />
        <Box ml="sp8" />
        {isSupportAdmin && <GroupsPurgeModal />}
      </Flex>
    );
  };

  const helperNotification = (): JSX.Element => {
    return (
      <Box ml="sp12" minW="300px" maxH="sp12">
        <InlineNotification
          notificationDescription="Please use search to show different results."
          notificationTitle={`Showing you ${tableResults.length} group(s)`}
          status="info"
        />
      </Box>
    );
  };

  const tableChildren = (): JSX.Element => {
    return (
      <Flex>
        <IdentitySearch<GroupUMS>
          searchFn={search}
          setLoading={setLoading}
          setState={setTableResults}
          initialSearch={initialSearch}
          pageSize={PAGE_SIZE}
          refresh={refresh}
          setRefresh={setRefresh}
        />
        {!loading && helperNotification()}
      </Flex>
    );
  };

  return (
    <TableBasedPage
      title="Groups"
      loaded={!loading}
      breadcrumbs={breadcrumbs}
      columns={columns}
      data={tableResults}
      tableDataMap={tableDataMap}
      children={aboveTableChildren()}
      childrenInTableHeader={tableChildren()}
      showSearch={false}
    />
  );
};

export default GroupsTable;
